import { Pause, Play } from "lucide-react";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { cn } from "@web/lib/utils.ts";
import { useGridPlayback } from "./GridPlaybackContext.tsx";
import { usePlayerStore } from "./playerStore.ts";

/**
 * Title cell: cover with a Spotify-style play overlay on hover.
 * Click plays this row; queue = currently loaded grid pages.
 */
export function TitlePlayCell({
  trackId,
  name,
  coverUrl,
  notLiked,
}: {
  trackId: string;
  name: string;
  coverUrl: string | null;
  /**
   * All-tracks view only. `undefined` = no slot.
   * When set, a fixed-width slot sits between cover and title so names line up;
   * `true` paints a muted not-liked dot, `false` leaves the slot empty.
   */
  notLiked?: boolean;
}) {
  const playback = useGridPlayback();
  const currentId = usePlayerStore((s) =>
    s.queueIndex >= 0 ? (s.queue[s.queueIndex]?.id ?? null) : null,
  );
  const playing = usePlayerStore((s) => s.playing);
  const isCurrent = currentId === trackId;
  const isThisPlaying = isCurrent && playing;
  const showNotLikedSlot = notLiked !== undefined;

  return (
    <div className="group/title flex min-w-0 items-center gap-3">
      <div className="relative size-8 shrink-0">
        <EntityAvatar src={coverUrl} alt="" size="sm" />
        <button
          type="button"
          className={cn(
            "absolute inset-0 flex items-center justify-center rounded-sm bg-black/60 text-text",
            "opacity-0 transition-opacity group-hover/title:opacity-100",
            "focus-visible:opacity-100 focus-visible:outline-none focus-visible:shadow-focus",
            isThisPlaying && "opacity-100",
          )}
          aria-label={isThisPlaying ? `Pause ${name}` : `Play ${name}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isCurrent) {
              void usePlayerStore.getState().togglePlay();
              return;
            }
            playback?.playFromTrack(trackId);
          }}
        >
          {isThisPlaying ? (
            <Pause className="size-3.5 fill-current" />
          ) : (
            <Play className="size-3.5 fill-current" />
          )}
        </button>
      </div>
      <div className="flex min-w-0 items-center gap-1.5">
        {showNotLikedSlot && (
          <span
            className="flex w-2 shrink-0 items-center justify-center"
            title={notLiked ? "Not in Liked Songs" : undefined}
            aria-label={notLiked ? "Not in Liked Songs" : undefined}
            aria-hidden={!notLiked}
          >
            {notLiked ? <span className="size-1 rounded-full bg-muted" /> : null}
          </span>
        )}
        <span className={cn("truncate font-medium", isCurrent && "text-accent")}>{name}</span>
      </div>
    </div>
  );
}
